import { DesignTemplate } from "@/api/config/chalice-types";
import { CUSTOM_SCHEDULE_ITEM_KEY } from "@/pages/Posts/PostCard/ScheduleCustomTime/useScheduleCustomTime";
import useUpdatePostActions, {
  LoadingType,
} from "pages/Posts/utils/useUpdatePostActions";
import { Post, PostStatus } from "store/posts/postConstants";
import { IntegrationType } from "store/user/userConstants";
import { trackEvent } from "utils/eventTracking/trackEvents";
import {
  EditorActionKey,
  PostEditingDetails,
  SaveDraftActionKey,
  ScheduleActionKey,
} from "../postEditorConstants";

const ACTION_REQUIRES_SAVE: EditorActionKey[] = [
  "save",
  "publish",
  "schedule",
  "schedule-next",
  "schedule-last",
] as const;

type UsePostEditorActionsProps = {
  existingPost?: Post;
  postDetail?: PostEditingDetails;
  refetch: () => Promise<any>;
  refetchAfterSchedule?: boolean;
  close: () => void;
  caption: string;
  mediaUrls: string[];
  template: DesignTemplate | undefined;
  publishTo: IntegrationType[] | null;
};

const usePostEditorActions = ({
  existingPost,
  postDetail,
  refetch,
  refetchAfterSchedule,
  close,
  caption,
  template,
  mediaUrls,
  publishTo,
}: UsePostEditorActionsProps) => {
  const {
    loading,
    createNewPost,
    savePost,
    duplicatePost,
    schedulePost,
    discardPost,
    downloadPost,
    publishPost,
  } = useUpdatePostActions();

  const getSavedPost = async ({
    showMessage,
    status,
  }: {
    showMessage: boolean;
    status?: PostStatus;
  }) => {
    if (!existingPost) {
      const savedPost = await createNewPost({
        caption,
        mediaUrls,
        publishTo,
        showMessage,
        template,
      });

      savedPost &&
        trackEvent("created_custom_post", {
          used_post_editor: false,
          post_type: savedPost.render_design_as,
        });

      return savedPost;
    } else if (
      existingPost.caption !== caption ||
      existingPost.media_urls !== mediaUrls ||
      existingPost.status !== status ||
      postDetail?.template !== template
    ) {
      if (template) {
        // we clear out the previews since we are rendering on backend
        // (if we try to save with the previews, the request is too large and fails)
        template = {
          ...template,
          // @ts-expect-error (I don't know why this is throwing an error but it works)
          pages: template?.pages.map((page) => ({
            ...page,
            custom: {
              ...page.custom,
              image_url: null,
            },
          })),
        };
        mediaUrls = [];
      }

      return savePost({
        post: existingPost,
        updateBody: {
          caption,
          template,
          media_urls: mediaUrls,
          publish_to: publishTo,
          status,
          should_render: true,
          review_status: null,
        },
        showMessage,
      });
    }

    return existingPost;
  };

  // Define a type for valid action keys excluding schedule variations initially
  // Includes 'publish' and 'schedule' (generic), excludes specific schedule actions
  type MappedActionKey =
    | Exclude<
        EditorActionKey,
        "schedule-next" | "schedule-last" | "schedule-custom"
      >
    | "schedule"; // Add generic schedule key

  const ACTION_HANDLER_MAP: {
    [key in MappedActionKey]?: (
      post: Post,
      action: EditorActionKey
    ) => Promise<any>;
  } = {
    download: downloadPost,
    delete: discardPost,
    publish: publishPost,
    duplicate: (post) =>
      duplicatePost({
        ...post,
        caption,
        template: postDetail?.template ?? null,
      }),
    schedule: (post, action) =>
      schedulePost({
        post,
        refetchAfterSchedule,
        scheduleType: action,
      }),
  };

  const handleAction = async (
    action:
      | SaveDraftActionKey
      | ScheduleActionKey
      | typeof CUSTOM_SCHEDULE_ITEM_KEY
  ) => {
    if (action === CUSTOM_SCHEDULE_ITEM_KEY) {
      return;
    }

    const isSaveDraftAction = action === "save";
    const exitAfterSave =
      isSaveDraftAction ||
      (action === "schedule" && existingPost?.status === "SCHEDULED");

    let isSuccess = false;
    let savedPost = existingPost;

    if (!savedPost || ACTION_REQUIRES_SAVE.includes(action)) {
      savedPost = await getSavedPost({
        showMessage: isSaveDraftAction,
        status: isSaveDraftAction ? "LIKED" : undefined,
      });

      isSuccess = Boolean(savedPost);
    }

    if (savedPost && !exitAfterSave) {
      const handlerKey: MappedActionKey = action.includes("schedule")
        ? "schedule"
        : (action as MappedActionKey);

      const actionHandler = ACTION_HANDLER_MAP[handlerKey];

      if (actionHandler) {
        isSuccess = await actionHandler(savedPost, action);
      } else {
        console.error(`No handler found for action: ${action}`);
        isSuccess = false;
      }
    } else if (isSuccess && exitAfterSave) {
      await refetch();
    }

    isSuccess && close();
  };

  return {
    loading: loading as LoadingType | false,
    getSavedPost,
    handleAction,
  };
};

export default usePostEditorActions;
